/* Draft-only enhancement of Shrine's existing shipping progress bar.
 * Shopify remains authoritative for shipping at checkout. No network requests,
 * cart mutations, hard-coded exchange rates or changes to Shrine's cart scripts.
 */
(() => {
  'use strict';
  const EU = new Set('AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE'.split(' '));
  const OTHER_EUROPE = new Set('NO CH AX AL AD BY BA FO GI GG IS IM JE XK LI MD MC ME MK RU SM RS SJ UA VA'.split(' '));

  function calculate({country, currency, activeCurrency, rate, total, shopCurrency = 'GBP', available = true}) {
    if (!available || !/^[A-Z]{2}$/.test(country || '') || !/^[A-Z]{3}$/.test(currency || '') || shopCurrency !== 'GBP' || !Number.isSafeInteger(total) || total < 0) return null;
    const region = country === 'GB' ? 'uk' : EU.has(country) ? 'eu' : OTHER_EUROPE.has(country) ? 'europe' : 'international';
    const base = {uk: 30, eu: 0, europe: 30, international: 30}[region];
    const fx = currency === 'GBP' ? 1 : Number(rate);
    if (base && (currency !== activeCurrency || !Number.isFinite(fx) || fx <= 0)) return null;
    // Shopify Liquid monetary amounts use hundredths, including JPY/KRW.
    const digits = new Intl.NumberFormat('en', {style: 'currency', currency}).resolvedOptions().maximumFractionDigits;
    const unit = digits === 0 ? 100 : 1;
    const threshold = base === 0 ? 0 : Math.ceil((base * fx * 100 - 1e-8) / unit) * unit;
    const remaining = Math.max(0, threshold - total);
    return {region, threshold, remaining, reached: remaining === 0, progress: threshold ? Math.min(100, total / threshold * 100) : 100};
  }

  function formatAmount({amount, currency, locale, foreign, moneySample}) {
    const options = {minimumFractionDigits: foreign || Number.isInteger(amount) ? 0 : 2, maximumFractionDigits: foreign ? 0 : 2};
    // Read the active currency's prefix/suffix from Shopify's own money filter.
    // This keeps labels such as Dhs., S$ and $ consistent with the cart prices,
    // without maintaining a currency-symbol table or changing eligibility math.
    const parts = typeof moneySample === 'string' && moneySample.match(/^(.*?)([0-9](?:[0-9.,'’\u00a0\u202f ]*[0-9])?)(.*?)$/);
    if (parts && parts[2].replace(/[^0-9]/g, '').startsWith('123456')) {
      return parts[1] + new Intl.NumberFormat(locale, options).format(amount) + parts[3];
    }
    return new Intl.NumberFormat(locale, {...options, style: 'currency', currency}).format(amount);
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = {calculate, formatAmount};
  if (typeof window === 'undefined' || !window.customElements || customElements.get('easytag-shipping-progress')) return;

  class ShippingProgress extends HTMLElement {
    connectedCallback() {
      // Native section HTML may be inserted while the parser is still inside it.
      queueMicrotask(() => this.render());
    }
    render() {
      if (!this.isConnected) return;
      const text = this.querySelector('[data-shipping-text]');
      const bar = this.querySelector('[data-shipping-bar]');
      const fill = this.querySelector('.cart-progress__bar__progress');
      const note = this.querySelector('[data-shipping-note]');
      const config = this.querySelector('[data-shipping-messages]');
      if (!text || !bar || !fill || !note || !config) return;
      try {
        const messages = JSON.parse(config.textContent);
        const state = calculate({country: this.dataset.country, currency: this.dataset.currency, total: Number(this.dataset.total), available: this.dataset.available === 'true', shopCurrency: this.dataset.shopCurrency, activeCurrency: window.Shopify?.currency?.active, rate: window.Shopify?.currency?.rate});
        if (!state || this.dataset.requiresShipping !== 'true') {
          text.textContent = messages.fallback;
          bar.hidden = true;
          this.dataset.state = 'fallback';
          return;
        }
        const locale = document.documentElement.lang || 'en';
        const foreign = this.dataset.currency !== 'GBP';
        // Round the displayed foreign-currency remainder UP to a whole unit;
        // retain full precision for eligibility/progress. Never show a false zero.
        const amount = foreign ? Math.ceil(state.remaining / 100) : state.remaining / 100;
        const formatted = formatAmount({amount, currency: this.dataset.currency, locale, foreign, moneySample: this.dataset.moneySample});
        let key;
        if (state.region === 'eu') key = 'eu_free';
        else if (state.region === 'uk') key = state.reached ? 'uk_free' : foreign ? 'uk_estimate' : 'uk_progress';
        else key = state.reached ? 'free' : foreign ? 'estimate' : 'progress';
        text.textContent = messages[key].replace('[amount]', formatted);
        note.textContent = (state.region === 'uk' ? messages.uk_note : messages.note).replace('[country]', this.dataset.countryName);
        bar.hidden = false;
        fill.style.width = `${state.progress}%`;
        bar.setAttribute('aria-valuenow', String(Math.floor(state.progress)));
        bar.setAttribute('aria-valuetext', text.textContent);
        this.dataset.state = state.reached ? 'reached' : 'progress';
        this.dataset.threshold = String(state.threshold);
        this.dataset.remaining = String(state.remaining);
      } catch (_) {
        // Leave the server-rendered policy fallback; never revive the old £30 promise.
        bar.hidden = true;
        this.dataset.state = 'fallback';
      }
    }
  }
  customElements.define('easytag-shipping-progress', ShippingProgress);
})();
